import {
	Calendar,
	Coins,
	Eye,
	MessageCircle,
	TrendingUp,
	Users,
} from "lucide-react";
import React from "react";
import { formatCurrency } from "../../../utils/formatCurrency";

interface SalesProperty {
	id: string;
	title: string;
	address: string;
	type: string;
	price: number;
	status: string;
	bedrooms: number;
	bathrooms: number;
	square_feet: number;
	images: string[];
	description: string;
	created_at: string;
	viewings: number;
	inquiries: number;
	viewingRequests?: number;
}

interface SalesStatsProps {
	properties?: SalesProperty[];
}

const SalesStats: React.FC<SalesStatsProps> = ({ properties = [] }) => {
	// Calculate statistics
	const totalListings = properties?.length;
	const activeListings = properties?.filter(
		(p) => p?.status === "active",
	)?.length;
	const occupiedProperties = properties?.filter(
		(p) => p?.status === "occupied",
	)?.length;
	const totalViewing = properties?.reduce(
		(sum, p) => sum + (p?.viewings || 0),
		0,
	);
	const totalInquiries = properties?.reduce(
		(sum, p) => sum + (p?.inquiries || 0),
		0,
	);

	const averagePrice =
		properties?.length > 0
			? properties?.reduce((sum, p) => {
					const price = Number(p?.price) || 0;
					return sum + price;
			  }, 0) / properties?.length
			: 0;

	const stats = [
		{
			title: "Total Listings",
			value: totalListings,
			icon: TrendingUp,
			color: "text-blue-600",
			bgColor: "bg-blue-50 dark:bg-blue-950/20",
			change: totalListings > 0 ? "+100%" : "0%",
		},
		{
			title: "Active Listings",
			value: activeListings,
			icon: Calendar,
			color: "text-green-600",
			bgColor: "bg-green-50 dark:bg-green-950/20",
			change: activeListings > 0 ? "+100%" : "0%",
		},
		{
			title: "Properties Occupied",
			value: occupiedProperties,
			icon: Coins,
			color: "text-purple-600",
			bgColor: "bg-purple-50 dark:bg-purple-950/20",
			change: occupiedProperties > 0 ? "+100%" : "0%",
		},
		{
			title: "Viewing Requests",
			value: totalViewing,
			icon: Eye,
			color: "text-orange-600",
			bgColor: "bg-orange-50 dark:bg-orange-950/20",
			change: totalViewing > 0 ? "+100%" : "0%",
		},
		{
			title: "Buyer Inquiries",
			value: totalInquiries,
			icon: MessageCircle,
			color: "text-pink-600",
			bgColor: "bg-pink-50 dark:bg-pink-950/20",
			change: totalInquiries > 0 ? "+100%" : "0%",
		},
		{
			title: "Average Price",
			value: formatCurrency(averagePrice, { showAbbreviation: true }),
			icon: Users,
			color: "text-indigo-600",
			bgColor: "bg-indigo-50 dark:bg-indigo-950/20",
			change: averagePrice > 0 ? "+100%" : "0%",
		},
	];

	return (
		<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
			{stats?.map((stat, index) => {
				const Icon = stat?.icon;
				return (
					<div
						key={index}
						className="bg-card border border-border rounded-lg p-4 sm:p-5 overflow-hidden"
					>
						<div className="flex items-center justify-between gap-2">
							<div className="flex-1 min-w-0">
								<p className="text-xs sm:text-sm font-medium text-muted-foreground mb-1 truncate">
									{stat?.title}
								</p>
								<p className="text-lg sm:text-xl font-bold text-foreground truncate">
									{stat?.value}
								</p>
								<p className="text-xs text-muted-foreground mt-1 truncate">
									{stat?.change} from last month
								</p>
							</div>
							<div className={`${stat?.bgColor} p-2 sm:p-2.5 rounded-lg flex-shrink-0`}>
								<Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${stat?.color}`} />
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default SalesStats;
